import { ExperienceType, OdProtosSellCommon_Product as Product } from '__generated__/globalTypes';
import type {
  SellerProductsExperiencesQuery_sellerProducts_SellerProductsQuery_productsExperiences_SellerProductExperienceResponse_products_Product_pricing as CashOfferDetails,
  SellerProductsExperiencesQuery_sellerProducts_SellerProductsQuery_productsExperiences_SellerProductExperienceResponse_products_Product_pricing_ListingPricing as ListingPricing,
  SellerProductsExperiencesQuery_sellerProducts_SellerProductsQuery_productsExperiences_SellerProductExperienceResponse_products_Product_assignedAgent_AssignedAgent as LwcAssignedAgent,
  SellerProductsExperiencesQuery_sellerProducts_SellerProductsQuery_productsExperiences_SellerProductExperienceResponse_products_Product_brokerage_Brokerage as LwcBrokerage,
  SellerCashOfferDetails_PricingsCashOfferDetails_Fragment as PricingsCashOfferDetails,
  SellerProductsExperiencesQuery_sellerProducts_SellerProductsQuery_productsExperiences_SellerProductExperienceResponse_products_SellDirectProduct as SellDirectProduct,
  SellerCashOfferDetails_UnderwritingCashOfferDetails_Fragment as UnderwritingCashOfferDetails,
} from 'sellDirect/graphql/__generated__/useGetSellerProductsExperiences';
import type { SellerProducts } from 'sellDirect/graphql/useGetSellerProductsExperiences';

export { Product };
export type { SellerProducts, ListingPricing, LwcAssignedAgent, LwcBrokerage, SellDirectProduct };

/** Products that are displayed to customers (excludes LIST_WITH_CERTAINTY) */
const AvailableProducts = [Product.OPENDOOR_DIRECT, Product.SELL_WITH_UPSIDE] as const;

export type AvailableProduct = (typeof AvailableProducts)[number];

export const AvailableProductNames: Record<AvailableProduct, string> = {
  [Product.OPENDOOR_DIRECT]: 'Cash offer',
  [Product.SELL_WITH_UPSIDE]: 'Cash Plus',
};

/**
 * Type guard to check if a product is displayable to customers.
 * Returns true for OPENDOOR_DIRECT and SELL_WITH_UPSIDE only.
 */
export const isAvailableProduct = (p: Product): p is AvailableProduct =>
  (AvailableProducts as readonly Product[]).includes(p);

/** Helper to determine if the customer is in the ALO Experience */
export const isAgentLedOffersExperience = (experiences: ExperienceType[]): boolean =>
  experiences.includes(ExperienceType.AGENT_LED_OFERS) ||
  experiences.includes(ExperienceType.AGENT_LED_OFFERS);

export const isPricingCashOfferDetails = (
  pricing?: SellDirectProduct['pricing'],
): pricing is PricingsCashOfferDetails => pricing?.__typename === 'PricingsCashOfferDetails';
const isUnderwritingCashOfferDetails = (
  pricing?: SellDirectProduct['pricing'],
): pricing is UnderwritingCashOfferDetails =>
  pricing?.__typename === 'UnderwritingCashOfferDetails';

/**
 * Extracts the pricing from a sell direct product (PricingsCashOfferDetails or UnderwritingCashOfferDetails)
 *
 * @param product a cash offer product to check for offer details
 * @returns CashOfferDetailsFields, if present
 */
export const getPricingsOrUnderwritingCashOfferDetails = (
  product?: SellDirectProduct,
): CashOfferDetails | undefined =>
  isPricingCashOfferDetails(product?.pricing) || isUnderwritingCashOfferDetails(product?.pricing)
    ? product.pricing
    : undefined;
